import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useDataProvider, useGetMany, useTranslate } from "ra-core";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

import { ListCardsSkeleton, ListEmptyState } from "../design/ListStates";
import { APP_LOCALE } from "../misc/locale";
import type { CrmDataProvider } from "../providers/types";
import { ROLE_LABELS } from "../sales/roles";
import type { AuditLogEntry, Sale } from "../types";

const EMPTY = "—";
const PAGE_SIZE = 100;

const formatWhen = (iso: string) =>
  new Date(iso).toLocaleString(APP_LOCALE, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

/** Columns holding a `sales` id, shown as the person's name. */
const SALES_COLUMNS = new Set([
  "sales_id",
  "advisor_at_validation",
  "created_by",
  "cancelled_by",
  "uploaded_by",
  "reviewed_by",
  "validated_by",
  "deleted_by",
]);

const ISO_DATETIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

const formatDate = (iso: string) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString(APP_LOCALE, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

const formatValue = (
  value: unknown,
  column: string,
  nameOf: (id: unknown) => string | undefined,
) => {
  if (value == null || value === "") return EMPTY;
  if (SALES_COLUMNS.has(column)) return nameOf(value) ?? String(value);
  if (typeof value === "string") {
    if (ISO_DATETIME.test(value)) return formatWhen(value);
    if (ISO_DATE.test(value)) return formatDate(value);
  }
  return String(value);
};

/** The columns an entry changed: the changed list on updates, every stored one on inserts. */
const changedColumns = (entry: AuditLogEntry): string[] =>
  entry.changed_columns ?? Object.keys(entry.new_data ?? {});

/**
 * Almikia Epic E5 (TASK-035): read-only "Historial" card of an operation, from
 * its append-only audit log. Only mounted for the roles that read the audit
 * log; the log never stores client personal fields. Shows the latest
 * `PAGE_SIZE` entries first and a "load more" control for older ones.
 */
export const OperationAuditHistory = ({
  operationId,
}: {
  operationId: string;
}) => {
  const translate = useTranslate();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const {
    data: entries,
    isPending,
    isError,
    refetch,
  } = useQuery({
    queryKey: ["operation-audit-log", operationId],
    queryFn: () => dataProvider.getOperationAuditLog(operationId),
  });

  const salesIds = [
    ...new Set(
      (entries ?? []).flatMap((entry) => {
        const ids: unknown[] = [entry.actor_sales_id];
        for (const data of [entry.old_data, entry.new_data]) {
          for (const column of SALES_COLUMNS) ids.push(data?.[column]);
        }
        return ids.filter(
          (id): id is string | number =>
            typeof id === "string" || typeof id === "number",
        );
      }),
    ),
  ];
  const { data: sales } = useGetMany<Sale>(
    "sales",
    { ids: salesIds },
    { enabled: salesIds.length > 0 },
  );
  const saleName = (id: unknown) => {
    const sale = (sales ?? []).find((s) => String(s.id) === String(id));
    return sale ? `${sale.first_name} ${sale.last_name}` : undefined;
  };
  const actorName = (entry: AuditLogEntry) =>
    saleName(entry.actor_sales_id) ??
    translate("crm.operations.audit.unknown_actor");

  // Latest entries first; the provider returns them oldest first.
  const ordered = [...(entries ?? [])].reverse();
  const shown = ordered.slice(0, visibleCount);

  return (
    <Card>
      <CardContent className="space-y-3" data-testid="operation-audit">
        <h3 className="font-semibold">
          {translate("crm.operations.audit.title")}
        </h3>
        {isPending ? (
          <ListCardsSkeleton rows={2} />
        ) : isError ? (
          <div role="alert" className="space-y-2 text-sm">
            <p>{translate("crm.operations.audit.error")}</p>
            <Button
              type="button"
              variant="outline"
              className="h-11 sm:h-9"
              onClick={() => refetch()}
            >
              {translate("crm.common.retry")}
            </Button>
          </div>
        ) : ordered.length === 0 ? (
          <ListEmptyState
            className="min-h-0 py-4"
            title={translate("crm.operations.audit.empty")}
            description={translate("crm.operations.audit.empty_hint")}
          />
        ) : (
          <>
            <ol className="space-y-3 text-sm">
              {shown.map((entry) => (
                <li key={entry.id} className="space-y-1">
                  <p>
                    <span className="tabular-nums text-muted-foreground">
                      {formatWhen(entry.occurred_at)}
                    </span>
                    {" · "}
                    <span className="font-medium">{actorName(entry)}</span>
                    {entry.actor_role
                      ? ` (${ROLE_LABELS[entry.actor_role] ?? entry.actor_role})`
                      : ""}
                  </p>
                  <p>
                    {translate(`crm.operations.audit.actions.${entry.action}`)}
                    {" · "}
                    {translate(
                      `crm.operations.audit.tables.${entry.table_name}`,
                      {
                        _: entry.table_name,
                      },
                    )}
                  </p>
                  <ul className="space-y-0.5 text-muted-foreground">
                    {changedColumns(entry).map((column) => (
                      <li key={column}>
                        <span className="text-foreground">
                          {translate(`crm.operations.audit.columns.${column}`, {
                            _: column,
                          })}
                        </span>
                        {": "}
                        {entry.action === "update" ? (
                          <>
                            {translate("crm.operations.audit.old_value")}{" "}
                            <span className="tabular-nums">
                              {formatValue(
                                entry.old_data?.[column],
                                column,
                                saleName,
                              )}
                            </span>
                            {" → "}
                            {translate("crm.operations.audit.new_value")}{" "}
                          </>
                        ) : null}
                        <span className="tabular-nums">
                          {formatValue(
                            entry.new_data?.[column],
                            column,
                            saleName,
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                  {entry.reason ? (
                    <p className="text-muted-foreground">
                      {translate("crm.operations.audit.reason", {
                        reason: entry.reason,
                      })}
                    </p>
                  ) : null}
                </li>
              ))}
            </ol>
            {ordered.length > shown.length ? (
              <Button
                type="button"
                variant="outline"
                className="h-11 sm:h-9"
                onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}
              >
                {translate("crm.operations.audit.load_more")}
              </Button>
            ) : null}
          </>
        )}
      </CardContent>
    </Card>
  );
};
